<template>
  <b-modal
    id="compose-mail"
    :visible="shallShowNotificationComposeModal"
    :modal-class="{ 'modal-sticky': sizeModal == 'lg' }"
    footer-class="d-flex justify-content-between"
    body-class="p-0"
    :size="sizeModal"
    scrollable
    no-close-on-backdrop
    static
    @change="(val) => $emit('update:show-notification-compose-modal', val)"
  >
    <!-- Modal Header -->
    <template #modal-header>
      <!-- Switch for active/desactivate notification -->
      <component
        :is="availableComponentActiveSwitch"
        ref="activeNotificationRef"
        :style="showLoader ? 'pointer-events: none; opacity: .5;' : ''"
        @add-active="composeData.activo = $event; showInactiveMessage = !$event"
      />

      <!-- Título -->
      <h5 class="modal-title">
        Nueva notificación
      </h5>

      <!-- Acciones de modal -->
      <div class="modal-actions d-inline">
        <div :style="showLoader ? 'pointer-events: none; opacity: .5;' : ''">
          <!-- Minimizar -->
          <span
            v-b-tooltip.hover="'Minimizar'"
            class="bullet bullet-warning cursor-pointer"
            @click="$emit('update:show-notification-compose-modal', false)"
          />
          <!-- Expandir -->
          <span
            v-if="sizeModal === 'lg'"
            v-b-tooltip.hover="'Expandir'"
            class="bullet bullet-success mx-50 cursor-pointer"
            @click="sizeModal = 'xl'"
          />
          <!-- Contraer -->
          <span
            v-else
            v-b-tooltip.hover="'Contraer'"
            class="bullet bullet-success mx-50 cursor-pointer"
            @click="sizeModal = 'lg'"
          />
          <!-- Cerrar -->
          <span
            v-b-tooltip.hover="'Cerrar'"
            class="bullet bullet-danger cursor-pointer"
            @click="discardCompose"
          />
        </div>
      </div>
    </template>

    <!-- Modal Footer -->
    <template #modal-footer>
      <!-- Footer: Left Content -->
      <div :style="showLoader ? 'pointer-events: none; opacity: .5;' : ''">
        <!-- Dropdown de prioridad -->
        <component
          :is="availableComponentPriorityDropdown"
          ref="priorityNotificationRef"
          @add-priority="composeData.prioridad = $event"
        />
      </div>

      <!-- Footer: Right Content -->
      <div :style="showLoader ? 'pointer-events: none; opacity: .5;' : ''">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          v-b-modal.confirm-modal-notification
          variant="primary"
        >
          Continuar
        </b-button>
      </div>
    </template>

    <b-overlay
      :show="showLoader"
      rounded="sm"
    >
      <!-- Modal: Body -->
      <b-form>
        <!-- Mensaje de notificación desactivada -->
        <b-alert
          v-height-fade.appear
          variant="danger"
          :show="showInactiveMessage"
        >
          <div class="alert-body text-center">
            <feather-icon
              icon="InfoIcon"
              class="mr-50"
            />
            <span>Esta notificación se encuentra <b>desactivada</b>, esto significa que se dará de alta, pero <b>no se notificará a los usuarios involucrados</b>.</span>
          </div>
        </b-alert>

        <!-- Dirigido a -->
        <component
          :is="availableComponentToField"
          ref="toNotificationRef"
        />

        <!-- Fecha de expiración -->
        <component
          :is="availableComponentExpireInput"
          ref="expireNotificationRef"
        />

        <!-- Título -->
        <div class="compose-mail-form-field">
          <label
            for="email-subject"
            class="font-weight-bolder"
          >Título: </label>
          <b-form-input
            id="email-subject"
            v-model="composeData.t"
            placeholder="Título de notificación"
          />
        </div>

        <!-- URL -->
        <component
          :is="availableComponentUrlInput"
          ref="urlNotificationRef"
          @add-url="composeData.url = $event"
        />

        <!-- Mensaje - Quill Editor -->
        <div class="message-editor mx-2 mt-50">
          <!-- Label -->
          <label
            for="quil-content"
            class="font-weight-bolder"
            style="font-size: 1rem;"
          >
            Mensaje:
          </label>
          <!-- Quill editor -->
          <quill-editor
            id="quil-content"
            v-model="composeData.m"
            :options="editorOption"
          />
          <!-- Barra de herramientas -->
          <div
            id="quill-toolbar"
            class="d-flex border-bottom-0"
          >
            <!-- Herramientas de edición de quill editor -->
            <button class="ql-bold" />
            <button class="ql-italic" />
            <button class="ql-underline" />
          </div>
        </div>
      </b-form>

      <!-- Modal de confirmación -->
      <notification-compose-confirm
        @cancel="$bvModal.hide('confirm-modal-notification');"
        @emit="$bvModal.hide('confirm-modal-notification'); saveNotification()"
      />
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormInput,
  BAvatar,
  BBadge,
  VBTooltip,
  BAlert,
  BCardText,
  BOverlay,
  BFormDatepicker,
} from 'bootstrap-vue'
import { heightFade } from '@core/directives/animations'
import Ripple from 'vue-ripple-directive'
import { ref } from '@vue/composition-api'
import { formhttp } from '@core/utils/utils'
import { quillEditor } from 'vue-quill-editor'
import vSelect from 'vue-select'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import NotificationComposeConfirm from './NotificationComposeConfirm.vue'
import useNotificationComponse from './useNotificationComponse'

const PriorityNotificationDropdown = () => import('./components/PriorityNotificationDropdown.vue')
const UrlNotificationInput = () => import('./components/UrlNotificationInput.vue')
const ExpireNotificationInput = () => import('./components/ExpireNotificationInput.vue')
const ActiveNotificationSwitch = () => import('./components/ActiveNotificationSwitch.vue')
const ToNotificationField = () => import('./components/ToNotificationField.vue')

export default {
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
    'height-fade': heightFade,
  },
  components: {
    // BSV
    BDropdown,
    BDropdownItem,
    BForm,
    BFormInput,
    BAvatar,
    BBadge,
    BAlert,
    BButton,
    BCardText,
    BOverlay,
    BFormDatepicker,

    // 3rd Party
    quillEditor,
    vSelect,

    // Notification
    NotificationComposeConfirm,

    // Dynamic Components
    ActiveNotificationSwitch,
    UrlNotificationInput,
    ExpireNotificationInput,
    PriorityNotificationDropdown,
    ToNotificationField,
  },
  model: {
    prop: 'shallShowNotificationComposeModal',
    event: 'update:show-notification-compose-modal',
  },
  props: {
    shallShowNotificationComposeModal: {
      type: Boolean,
      required: true,
    },
  },
  setup(_, { emit }) {
    const toast = useToast()
    const composeData = ref({}) // * Compose data
    // * Dynamic components refs
    const activeNotificationRef = ref(null)
    const toNotificationRef = ref(null)
    const urlNotificationRef = ref(null)
    const priorityNotificationRef = ref(null)
    const expireNotificationRef = ref(null)

    const showLoader = ref(false)
    const showNotificationPreview = ref(false) // * Flag to show notification preview, before send
    const showInactiveMessage = ref(false) // * Flag to show inactive notification message
    const sizeModal = ref('lg') // * Modal size
    const {
      availableComponentPriorityDropdown,
      availableComponentActiveSwitch,
      availableComponentUrlInput,
      availableComponentExpireInput,
      availableComponentToField,
      saveData,
    } = useNotificationComponse()

    // * Quill editor options
    const editorOption = {
      modules: {
        toolbar: '#quill-toolbar',
      },
      placeholder: 'Escribe aquí',
    }

    // * Discard Compose
    const discardCompose = () => {
      if (activeNotificationRef.value !== null) activeNotificationRef.value.resetComponent()
      if (toNotificationRef.value !== null) toNotificationRef.value.resetComponent()
      if (urlNotificationRef.value !== null) urlNotificationRef.value.resetComponent()
      if (priorityNotificationRef.value !== null) priorityNotificationRef.value.resetComponent()
      if (expireNotificationRef.value !== null) expireNotificationRef.value.resetComponent()

      composeData.value = {}
      emit('update:show-notification-compose-modal', false)
    }

    // * Save changes
    const saveNotification = () => {
      showLoader.value = true

      const data = {
        action: 3,
        t: JSON.stringify({ data: composeData.value.t }),
        m: JSON.stringify({ data: composeData.value.m }),
      }

      if (activeNotificationRef.value !== null) data.s = activeNotificationRef.value.active
      if (urlNotificationRef.value !== null) data.u = JSON.stringify(urlNotificationRef.value.url)
      if (priorityNotificationRef.value !== null) data.p = priorityNotificationRef.value.prioridadSelected
      if (expireNotificationRef.value !== null && expireNotificationRef.value.expireDate !== null) data.expire = expireNotificationRef.value.expireDate
      if (toNotificationRef.value !== null) {
        if (toNotificationRef.value.toSelected && toNotificationRef.value.toSelected !== null) data.to = toNotificationRef.value.toSelected
        if (toNotificationRef.value.idReferencialk && toNotificationRef.value.idReferencialk !== null) data.toRef = toNotificationRef.value.idReferencialk
        if (toNotificationRef.value.idPlan && toNotificationRef.value.idPlan !== null) data.toPlan = toNotificationRef.value.idPlan
      }

      saveData(data)
        .then(response => {
          const { icon, label } = formhttp(response.status)
          showLoader.value = false
          if (response.status === 200) discardCompose()

          toast({
            component: ToastificationContent,
            props: {
              title: 'Resultado de petición',
              text: response.message,
              icon,
              variant: label,
            },
          },
          { timeout: 8000 })
        }, error => {
          showLoader.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Resultado de petición',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 8000 })
        })
    }

    return {
      // Data
      composeData,
      sizeModal,
      showInactiveMessage,
      showNotificationPreview,

      // Ref
      activeNotificationRef,
      urlNotificationRef,
      priorityNotificationRef,
      toNotificationRef,
      expireNotificationRef,

      // Components
      availableComponentPriorityDropdown,
      availableComponentActiveSwitch,
      availableComponentUrlInput,
      availableComponentExpireInput,
      availableComponentToField,

      // Filter
      showLoader,

      // Notification actions
      saveNotification,
      discardCompose,

      // Extra
      editorOption,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/quill.scss';
</style>

<style lang="scss" scoped>
form ::v-deep {

  // Mail To vue-select style
  .v-select {
    .vs__dropdown-toggle {
      border: 0;
      box-shadow: none;
    }
    .vs__open-indicator {
      display: none;
    }
  }

  // Quill Editor Style
  .quill-editor {
    .ql-container.ql-snow {
      border-bottom: 0 !important;
    }
  }
}
</style>
